import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchMockTests,
  fetchMockTestDetails,
  startMockTest,
  saveMockTestAnswer,
  submitMockTest,
  abandonMockTest,
  fetchMockTestResult,
} from "./nativeLearning";

/**
 * Standalone Live/Mock Test experience for the learner app — list → pre-start →
 * timed runner → result. The server owns the clock; this only counts down the
 * remaining_seconds it reports, autosaves each answer, and submits. Themed off
 * the membership's `primary` colour.
 */

/* ───────────────────────────── theming ───────────────────────────── */
// Darken/lighten a hex colour so buttons/badges can use a rich gradient built
// from the single brand colour. pct < 0 darkens.
function shade(hex, pct) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  const f = 1 + pct;
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  const r = c((n >> 16) & 255);
  const g = c((n >> 8) & 255);
  const b = c(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
// Glossy, dimensional gradient from the single brand colour — lighter at the
// top-left, deepening toward the bottom-right, so badges/buttons read as a
// polished "gem" surface rather than a flat fill.
const grad = (p) => `linear-gradient(150deg, ${shade(p, 0.18)} 0%, ${p} 44%, ${shade(p, -0.26)} 100%)`;
// A soft white top-sheen layered over the colour gradient — used on the primary
// CTA buttons so they look slightly convex and premium.
const btnBg = (p) => `linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 45%), ${grad(p)}`;
// Premium card: hairline ring + a layered, soft ambient shadow that lifts the
// surface off the page (works in light and dark).
const CARD =
  "rounded-2xl border border-gray-200/70 dark:border-white/10 bg-surface shadow-[0_1px_2px_rgba(16,24,40,.05),0_2px_8px_-3px_rgba(16,24,40,.08),0_26px_50px_-28px_rgba(16,24,40,.30)]";

function fmtDateTime(s) {
  if (!s) return "";
  const m = String(s).replace("T", " ").match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return String(s);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let hr = parseInt(m[4], 10);
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${months[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${hr}:${m[5]} ${ampm} IST`;
}
function fmtClock(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}:${String(m % 60).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  }
  return `${m}:${String(r).padStart(2, "0")}`;
}

const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
  </svg>
);
const FlagIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4h11l-1.5 3L15 10H4" />
  </svg>
);
const Check = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
const DocIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
  </svg>
);

/* ═══════════════════════════ container ═══════════════════════════ */
export default function MockTestSection({ funnelId, memberId, jwt, primary = "#0274F7", page = null, courseId = null, groupToken = null }) {
  const ctx = useMemo(() => ({ funnelId, memberId, jwt }), [funnelId, memberId, jwt]);

  const [screen, setScreen] = useState("list");
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeTestId, setActiveTestId] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);

  const loadList = useCallback(() => {
    setLoading(true);
    setError("");
    fetchMockTests(ctx)
      .then((d) => {
        const all = Array.isArray(d.mock_tests) ? d.mock_tests : [];
        const shown = page
          ? all.filter((t) => {
              if (t.is_closed) return false;
              const p = Array.isArray(t.display_pages) && t.display_pages.length ? t.display_pages : ["courses"];
              if (!p.includes(page)) return false;
              if (page === "courses") {
                const cids = Array.isArray(t.course_ids) ? t.course_ids : [];
                return courseId ? cids.includes(courseId) : cids.length === 0;
              }
              if (page === "community") {
                const gtokens = Array.isArray(t.group_tokens) ? t.group_tokens : [];
                if (gtokens.length === 0) return true;
                return groupToken != null && gtokens.includes(groupToken);
              }
              return true;
            })
          : all;
        setTests(shown);
      })
      .catch(() => setError("Could not load tests. Please try again."))
      .finally(() => setLoading(false));
  }, [ctx, page, courseId, groupToken]);

  useEffect(() => { loadList(); }, [loadList]);

  const openPreStart = (testId) => { setActiveTestId(testId); setScreen("prestart"); };
  const onBeganAttempt = (payload) => { setAttempt(payload); setScreen("running"); };
  const onFinished = (res) => { setResult(res); setAttempt(null); setScreen("result"); };
  const backToList = () => { setActiveTestId(null); setAttempt(null); setResult(null); setScreen("list"); loadList(); };

  return (
    <div className="min-h-[60vh]">
      {screen === "list" ? (
        <TestListScreen tests={tests} loading={loading} error={error} primary={primary} onRetry={loadList} onOpen={openPreStart} />
      ) : screen === "prestart" ? (
        <PreStartScreen ctx={ctx} testId={activeTestId} primary={primary} onBack={backToList} onBegan={onBeganAttempt} />
      ) : screen === "running" ? (
        <RunnerScreen ctx={ctx} initial={attempt} primary={primary} onFinished={onFinished} />
      ) : (
        <ResultScreen result={result} primary={primary} onDone={backToList} />
      )}
    </div>
  );
}

/* ═══════════════════════════ list screen ═══════════════════════════ */
function TestListScreen({ tests, loading, error, primary, onRetry, onOpen }) {
  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200" style={{ borderTopColor: primary }} />
        <p className="mt-3 text-sm text-gray-400">Loading tests…</p>
      </div>
    );
  if (error)
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-gray-500">{error}</p>
        <button onClick={onRetry} className="mt-3 rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">Retry</button>
      </div>
    );
  if (tests.length === 0)
    return (
      <div className="py-24 text-center">
        <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl text-white shadow-lg" style={{ background: grad(primary), boxShadow: `0 12px 26px -12px ${primary}` }}>
          <ClockIcon className="w-8 h-8" />
        </div>
        <p className="text-base font-bold text-gray-900">No mock tests available</p>
        <p className="mt-1 text-[13px] text-gray-400">Check back later — your instructor may add timed tests here.</p>
      </div>
    );

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {tests.map((t) => {
        const exhausted = t.attempts_left === 0;
        const upcoming = t.available_now === false;
        const disabled = upcoming || exhausted;
        return (
          <div key={t.mock_test_id} className={`group flex flex-col overflow-hidden ${CARD} transition-transform duration-200 hover:-translate-y-0.5`}>
            {/* accent header strip */}
            <div className="relative overflow-hidden px-5 pt-5" style={{ background: `linear-gradient(180deg, ${primary}12, transparent)` }}>
              <div className="pointer-events-none absolute -right-7 -top-9 h-28 w-28 rounded-full opacity-[0.12] blur-2xl" style={{ background: primary }} />
              <div className="relative flex items-start justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-2xl text-white ring-1 ring-white/25" style={{ background: grad(primary), boxShadow: `0 10px 22px -8px ${primary}, inset 0 1px 0 rgba(255,255,255,.28)` }}>
                  <ClockIcon className="w-5 h-5" />
                </span>
                {upcoming ? (
                  <Badge tone="blue">Upcoming</Badge>
                ) : t.passed ? (
                  <Badge tone="green">Passed</Badge>
                ) : null}
              </div>
              <h3 className="mt-3 text-[16px] font-extrabold leading-snug tracking-tight text-gray-900">{t.name}</h3>
              {t.description ? <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-gray-500">{t.description}</p> : null}
            </div>

            <div className="flex flex-1 flex-col px-5 pb-5 pt-4">
              <div className="flex flex-wrap gap-1.5">
                <Pill icon={<ClockIcon className="w-3.5 h-3.5" />}>{t.duration_minutes} min</Pill>
                <Pill>{t.total_questions} Qs</Pill>
                <Pill>Pass {t.pass_percentage}%</Pill>
              </div>
              <p className="mt-3 text-[11.5px] text-gray-400">
                {upcoming ? (
                  <span className="font-medium text-blue-600 dark:text-blue-300">Opens {fmtDateTime(t.available_from)}</span>
                ) : (
                  <>
                    {t.attempts_left === null ? "Unlimited attempts" : `${t.attempts_left} attempt(s) left`}
                    {t.attempts_used > 0 ? ` · best ${t.best_percentage}%` : ""}
                    {t.available_until ? <span className="text-amber-600 dark:text-amber-300"> · Closes {fmtDateTime(t.available_until)}</span> : ""}
                  </>
                )}
              </p>

              <button
                onClick={() => onOpen(t.mock_test_id)}
                disabled={disabled}
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-sm font-bold text-white transition-all hover:brightness-[1.06] active:scale-[.98] disabled:cursor-not-allowed disabled:hover:brightness-100"
                style={disabled ? { background: "rgb(var(--c-gray-200))", color: "rgb(var(--c-gray-400))" } : { background: btnBg(primary), boxShadow: `0 12px 26px -10px ${primary}` }}
              >
                {upcoming ? "Not open yet" : exhausted ? "No attempts left" : "Start test"}
                {!disabled ? (
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                ) : null}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function Badge({ tone, children }) {
  const map = {
    blue: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-300 ring-blue-100 dark:ring-blue-500/30",
    green: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-300 ring-green-100 dark:ring-green-500/30",
    amber: "bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 ring-amber-100 dark:ring-amber-500/30",
  };
  return <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${map[tone] || map.blue}`}>{children}</span>;
}
function Pill({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[11.5px] font-semibold text-gray-600 shadow-[0_1px_2px_rgba(16,24,40,.05)] ring-1 ring-gray-200/80 dark:bg-white/5 dark:text-gray-300 dark:ring-white/10 dark:shadow-none">
      {icon}
      {children}
    </span>
  );
}

/* ═══════════════════════════ pre-start ═══════════════════════════ */
function PreStartScreen({ ctx, testId, primary, onBack, onBegan }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchMockTestDetails(ctx, testId)
      .then((d) => alive && setDetails(d.success ? d : null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [ctx, testId]);

  const begin = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = await startMockTest(ctx, testId);
      if (payload.success && payload.attempt_id) onBegan(payload);
      else setErr(payload.message || "Could not start the test.");
    } catch {
      setErr("Could not start the test. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center py-24">
        <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200" style={{ borderTopColor: primary }} />
      </div>
    );
  if (!details)
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-gray-500">This test is unavailable.</p>
        <button onClick={onBack} className="mt-3 rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Back</button>
      </div>
    );

  const t = details.test;
  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-gray-800">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        Back to tests
      </button>
      <div className={`overflow-hidden ${CARD}`}>
        {/* hero */}
        <div className="relative overflow-hidden px-6 pt-7 sm:px-8" style={{ background: `linear-gradient(180deg, ${primary}18, transparent)` }}>
          <div className="pointer-events-none absolute -right-12 -top-14 h-44 w-44 rounded-full opacity-[0.10] blur-3xl" style={{ background: primary }} />
          <span className="relative grid h-14 w-14 place-items-center rounded-2xl text-white ring-1 ring-white/25" style={{ background: grad(primary), boxShadow: `0 16px 30px -12px ${primary}, inset 0 1px 0 rgba(255,255,255,.28)` }}>
            <ClockIcon className="w-7 h-7" />
          </span>
          <h2 className="relative mt-4 text-2xl font-extrabold tracking-tight text-gray-900">{t.name}</h2>
          {t.description ? <p className="relative mt-1.5 text-sm leading-relaxed text-gray-500">{t.description}</p> : null}
        </div>

        <div className="px-6 pb-7 pt-5 sm:px-8">
          <div className="grid grid-cols-3 gap-3">
            <Stat primary={primary} label="Duration" value={`${t.duration_minutes} min`} />
            <Stat primary={primary} label="Questions" value={t.total_questions} />
            <Stat primary={primary} label="Pass mark" value={`${t.pass_percentage}%`} />
          </div>

          <div className="mt-5 flex gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white" style={{ background: grad(primary) }}>
              <DocIcon className="w-4 h-4" />
            </span>
            <div className="text-[13px] text-gray-600">
              <p className="font-bold text-gray-800">Instructions</p>
              {t.instructions ? (
                <p className="mt-0.5 whitespace-pre-line leading-relaxed">{t.instructions}</p>
              ) : (
                <ul className="mt-1 list-inside list-disc space-y-1">
                  <li>The timer starts as soon as you begin and can't be paused.</li>
                  <li>Your answers save automatically as you go.</li>
                  <li>The test submits itself when the time runs out.</li>
                  {t.allow_navigation ? <li>You can move freely between questions.</li> : <li>You can't return to a previous question.</li>}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-[12.5px] text-gray-400">
            <ClockIcon className="w-4 h-4" />
            {details.attempts_left === null ? "Unlimited attempts." : `${details.attempts_left} attempt(s) remaining.`}
          </div>

          {/* One-sitting rule — set expectations before the timer starts. */}
          <div className="mt-4 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
            <svg className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a1.5 1.5 0 0 0 1.29 2.25h17.78A1.5 1.5 0 0 0 22.18 18L13.71 3.86a1.5 1.5 0 0 0-2.42 0z" />
            </svg>
            <p className="text-[12.5px] leading-relaxed text-amber-800 dark:text-amber-200">
              <b className="font-bold text-amber-900 dark:text-amber-100">Finish in one sitting.</b> Once you start, closing this window, refreshing, or navigating away will <b>submit your test automatically</b> — you can't pause and resume it later.
            </p>
          </div>

          {err ? <p className="mt-3 text-sm font-medium text-red-500">{err}</p> : null}

          <button
            onClick={begin}
            disabled={busy}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-all hover:brightness-[1.06] active:scale-[.99] disabled:opacity-60"
            style={{ background: btnBg(primary), boxShadow: `0 14px 30px -10px ${primary}` }}
          >
            {busy ? "Starting…" : "Start test now"}
            {busy ? null : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, primary }) {
  return (
    <div className="rounded-2xl border border-gray-200/70 bg-gradient-to-b from-surface to-gray-50/70 py-3.5 text-center shadow-[0_1px_2px_rgba(16,24,40,.04)] dark:border-white/10 dark:from-white/[.03] dark:to-transparent dark:shadow-none">
      <div className="text-xl font-extrabold tracking-tight" style={{ color: primary ? shade(primary, -0.1) : "rgb(var(--c-gray-900))" }}>{value}</div>
      <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
    </div>
  );
}

/* ═══════════════════════════ runner ═══════════════════════════ */
function RunnerScreen({ ctx, initial, primary, onFinished }) {
  const questions = initial.questions || [];
  const cfg = initial.config || {};
  const allowNav = cfg.allow_navigation !== false;
  const allowReview = cfg.allow_mark_review !== false;

  const [answers, setAnswers] = useState(() => {
    const seed = {};
    (initial.answers || []).forEach((a) => {
      const vals = Array.isArray(a.values) ? a.values : a.choice_rand_id ? [a.choice_rand_id] : [];
      seed[a.bank_question_id] = { values: vals, marked: !!a.marked_for_review };
    });
    return seed;
  });
  const [idx, setIdx] = useState(0);
  const totalSecs = Math.max(1, (initial.duration_minutes || 0) * 60 || Number(initial.remaining_seconds || 0));
  const [remaining, setRemaining] = useState(Math.max(0, Number(initial.remaining_seconds || 0)));
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const deadlineRef = useRef(Date.now() + Math.max(0, Number(initial.remaining_seconds || 0)) * 1000);
  const finishedRef = useRef(false);

  const finish = useCallback(
    async () => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      setSubmitting(true);
      try {
        onFinished(await submitMockTest(ctx, initial.attempt_id));
      } catch {
        try {
          onFinished(await fetchMockTestResult(ctx, initial.attempt_id));
        } catch {
          finishedRef.current = false;
          setSubmitting(false);
        }
      }
    },
    [ctx, initial.attempt_id, onFinished]
  );

  useEffect(() => {
    const id = setInterval(() => {
      const rem = Math.round((deadlineRef.current - Date.now()) / 1000);
      setRemaining(Math.max(0, rem));
      if (rem <= 0) { clearInterval(id); finish(); }
    }, 1000);
    return () => clearInterval(id);
  }, [finish]);

  // A mock test is one continuous sitting: leaving mid-test auto-submits the
  // attempt (it can't be resumed). Fire the abandon request on ANY exit —
  //  - page unload (close tab / refresh / hard navigate): pagehide (keepalive
  //    request survives the unload); beforeunload also warns first.
  //  - SPA-internal navigation or closing the popup: the cleanup on unmount.
  // finishedRef guards it so a normal / timed submit never double-fires, and the
  // `armed` flag makes the unmount handler ignore React StrictMode's dev
  // mount→unmount→mount double-invoke (which would otherwise abandon on start).
  useEffect(() => {
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 0);
    const submitLeave = () => { if (!finishedRef.current) abandonMockTest(ctx, initial.attempt_id); };
    const onBefore = (e) => { if (!finishedRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    window.addEventListener("pagehide", submitLeave);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener("beforeunload", onBefore);
      window.removeEventListener("pagehide", submitLeave);
      if (armed) submitLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = useCallback(
    async (qid, values, marked) => {
      try {
        const res = await saveMockTestAnswer(ctx, { attemptId: initial.attempt_id, bankQuestionId: qid, selectedValues: values, choiceRandId: values.length === 1 ? values[0] : "", markedForReview: marked });
        if (res && res.success === false && Number(res.response) === 410) {
          finishedRef.current = true;
          onFinished(res.result || (await fetchMockTestResult(ctx, initial.attempt_id)));
        }
      } catch {
        /* best-effort */
      }
    },
    [ctx, initial.attempt_id, onFinished]
  );

  const q = questions[idx];
  const setAnswer = (values) => {
    const cur = answers[q.question_id] || {};
    const next = { values, marked: !!cur.marked };
    setAnswers((a) => ({ ...a, [q.question_id]: next }));
    persist(q.question_id, values, next.marked);
  };
  const chooseSingle = (rand) => setAnswer([rand]);
  const toggleMulti = (rand) => {
    const cur = answers[q.question_id]?.values || [];
    setAnswer(cur.includes(rand) ? cur.filter((x) => x !== rand) : [...cur, rand]);
  };
  const setNumeric = (val) => setAnswer(String(val).trim() === "" ? [] : [String(val)]);
  const toggleReview = () => {
    const cur = answers[q.question_id] || {};
    const next = { values: cur.values || [], marked: !cur.marked };
    setAnswers((a) => ({ ...a, [q.question_id]: next }));
    persist(q.question_id, next.values, next.marked);
  };

  const goTo = (i) => {
    // Navigation locked (allow_navigation off): you can't return to a previous
    // question — only the Next button moves you forward, one at a time.
    if (!allowNav && i < idx) return;
    setIdx(Math.max(0, Math.min(questions.length - 1, i)));
  };
  const answeredCount = Object.values(answers).filter((a) => (a.values || []).length > 0).length;
  const lowTime = remaining <= 60;
  const progress = Math.max(0, Math.min(100, (remaining / totalSecs) * 100));

  return (
    <div className="mx-auto max-w-5xl">
      {/* sticky timer bar */}
      <div className={`sticky top-0 z-20 mb-4 overflow-hidden ${CARD}`}>
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl text-white ring-1 ring-white/20" style={{ background: grad(primary), boxShadow: `inset 0 1px 0 rgba(255,255,255,.25)` }}>
              <span className="text-[13px] font-extrabold">{idx + 1}</span>
            </span>
            <div className="text-sm font-bold text-gray-800">
              Question {idx + 1} <span className="font-medium text-gray-400">of {questions.length}</span>
            </div>
          </div>
          <div
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-extrabold tabular-nums ${lowTime ? "animate-pulse bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-300 ring-1 ring-red-100 dark:ring-red-500/30" : "text-white ring-1 ring-white/20"}`}
            style={lowTime ? undefined : { background: grad(primary), boxShadow: `0 8px 18px -8px ${primary}, inset 0 1px 0 rgba(255,255,255,.22)` }}
          >
            <ClockIcon className="w-4 h-4" />
            {fmtClock(remaining)}
          </div>
        </div>
        {/* time progress */}
        <div className="h-1 w-full bg-gray-100">
          <div className="h-full transition-[width] duration-1000 ease-linear" style={{ width: `${progress}%`, background: lowTime ? "#ef4444" : primary }} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_248px]">
        {/* question card */}
        <div className={`${CARD} p-5 sm:p-7`}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <p className="text-[16px] font-semibold leading-relaxed text-gray-900">
              <span className="mr-2 font-extrabold" style={{ color: primary }}>{idx + 1}.</span>
              {q.question}
            </p>
            {allowReview ? (
              <button
                onClick={toggleReview}
                title="Mark for review"
                className={`shrink-0 rounded-xl p-2 transition ${answers[q.question_id]?.marked ? "bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300" : "text-gray-300 dark:text-gray-500 hover:bg-gray-100 hover:text-gray-500"}`}
              >
                <FlagIcon />
              </button>
            ) : null}
          </div>

          {q.question_type === "numerical" ? (
            <div>
              <input
                type="number"
                step="any"
                value={(answers[q.question_id]?.values || [])[0] ?? ""}
                onChange={(e) => setNumeric(e.target.value)}
                placeholder="Type your answer"
                className="w-full max-w-xs rounded-xl border-2 border-gray-200 px-4 py-3 text-[15px] font-semibold text-gray-900 outline-none transition focus:ring-4"
                style={{ caretColor: primary }}
                onFocus={(e) => { e.target.style.borderColor = primary; e.target.style.boxShadow = `0 0 0 4px ${primary}22`; }}
                onBlur={(e) => { e.target.style.borderColor = ""; e.target.style.boxShadow = ""; }}
              />
              <p className="mt-2 text-[12px] text-gray-400">Enter a number.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {q.question_type === "multiple" ? (
                <p className="mb-1 inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-500">Select all that apply</p>
              ) : null}
              {q.choices.map((c, ci) => {
                const vals = answers[q.question_id]?.values || [];
                const on = vals.includes(c.choice_rand_id);
                const multi = q.question_type === "multiple";
                const letter = String.fromCharCode(65 + ci);
                return (
                  <button
                    key={c.choice_rand_id}
                    onClick={() => (multi ? toggleMulti(c.choice_rand_id) : chooseSingle(c.choice_rand_id))}
                    className={`flex w-full items-center gap-3.5 rounded-2xl border-2 px-4 py-3.5 text-left text-[15px] transition-all active:scale-[.995] ${on ? "" : "hover:bg-gray-50/70 dark:hover:bg-white/[.03]"}`}
                    style={
                      on
                        ? { borderColor: primary, background: `${primary}12`, color: shade(primary, -0.1), fontWeight: 600, boxShadow: `0 8px 20px -12px ${primary}` }
                        : { borderColor: "rgb(var(--c-gray-200))" }
                    }
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center text-[13px] font-extrabold text-gray-500 ${multi ? "rounded-lg" : "rounded-full"} bg-gray-100 dark:bg-white/10`}
                      style={on ? { background: grad(primary), color: "#fff", boxShadow: `inset 0 1px 0 rgba(255,255,255,.28)` } : undefined}
                    >
                      {on ? <Check className="w-4 h-4" /> : letter}
                    </span>
                    <span className="min-w-0 flex-1 text-gray-800">{c.choice_name}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-7 flex items-center justify-between border-t border-gray-100 pt-5">
            <button
              onClick={() => goTo(idx - 1)}
              disabled={!allowNav || idx === 0}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Previous
            </button>
            {idx < questions.length - 1 ? (
              <button onClick={() => goTo(idx + 1)} className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold text-white transition active:scale-[.98]" style={{ background: grad(primary), boxShadow: `0 8px 18px -8px ${primary}` }}>
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            ) : (
              <button onClick={() => setConfirming(true)} className="rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 px-6 py-2.5 text-sm font-bold text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,.8)] transition active:scale-[.98]">
                Submit test
              </button>
            )}
          </div>
        </div>

        {/* palette */}
        <div className={`${CARD} p-4 lg:sticky lg:top-24 lg:self-start`}>
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400">Question palette</p>
          <div className="grid grid-cols-6 gap-2 lg:grid-cols-5">
            {questions.map((qq, i) => {
              const a = answers[qq.question_id] || {};
              const isCur = i === idx;
              let cls = "bg-gray-100 text-gray-500";
              let st;
              if (a.marked) cls = "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30";
              else if ((a.values || []).length > 0) { cls = "text-white"; st = { background: grad(primary) }; }
              return (
                <button
                  key={qq.question_id}
                  onClick={() => goTo(i)}
                  disabled={!allowNav}
                  title={allowNav ? undefined : "Jumping is disabled — use Next to move forward"}
                  className={`grid h-9 w-9 place-items-center rounded-xl text-[13px] font-bold transition ${cls} ${allowNav ? "" : "cursor-not-allowed"}`}
                  style={{ ...(st || {}), ...(isCur ? { boxShadow: `0 0 0 2px rgb(var(--c-surface)), 0 0 0 4px ${primary}` } : {}) }}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1.5 text-[11.5px] text-gray-500">
            <Legend color={primary} label={`Answered (${answeredCount})`} />
            <Legend className="bg-amber-300" label="Marked for review" />
            <Legend className="bg-gray-200" label="Not answered" />
          </div>
          <button onClick={() => setConfirming(true)} className="mt-4 w-full rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,.8)] transition active:scale-[.98]">
            Submit test
          </button>
        </div>
      </div>

      {confirming ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-sm ${CARD} p-6 text-center`}>
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl text-white" style={{ background: grad(primary) }}>
              <Check className="w-6 h-6" />
            </div>
            <h4 className="text-lg font-extrabold text-gray-900">Submit your test?</h4>
            <p className="mt-1 text-sm text-gray-500">
              You've answered <b className="text-gray-800">{answeredCount}</b> of <b className="text-gray-800">{questions.length}</b> questions.
              {answeredCount < questions.length ? " Unanswered questions score zero." : ""}
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirming(false)} disabled={submitting} className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">Keep going</button>
              <button onClick={finish} disabled={submitting} className="flex-1 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 px-4 py-3 text-sm font-bold text-white transition disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
function Legend({ color, className = "", label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-3.5 w-3.5 rounded-md ${className}`} style={color ? { background: grad(color) } : undefined} />
      {label}
    </div>
  );
}

/* ═══════════════════════════ result ═══════════════════════════ */
function ResultScreen({ result, primary, onDone }) {
  if (!result)
    return (
      <div className="py-24 text-center">
        <p className="text-sm text-gray-500">No result to show.</p>
        <button onClick={onDone} className="mt-3 rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Back to tests</button>
      </div>
    );

  // Two independent creator controls: "Reveal score" and "Answer review".
  const showScore = result.show_result;
  const showReview = result.show_review;
  const passed = result.passed;
  const pct = Math.max(0, Math.min(100, Number(result.percentage) || 0));
  const ring = `conic-gradient(${passed ? "#22c55e" : "#ef4444"} ${pct * 3.6}deg, rgb(var(--c-gray-200)) 0deg)`; // track follows the theme

  return (
    <div className="mx-auto max-w-2xl">
      <div className={`overflow-hidden ${CARD}`}>
        {showScore ? (
          <div className="px-6 pt-8 pb-6 text-center sm:px-10" style={{ background: `linear-gradient(180deg, ${passed ? "#22c55e14" : "#ef444414"}, transparent)` }}>
            {/* score ring */}
            <div className="mx-auto grid h-32 w-32 place-items-center rounded-full" style={{ background: ring, boxShadow: `0 14px 32px -16px ${passed ? "#22c55e" : "#ef4444"}` }}>
              <div className="grid h-[104px] w-[104px] place-items-center rounded-full bg-surface shadow-[inset_0_1px_3px_rgba(16,24,40,.07)]">
                <div className="text-center">
                  <div className="text-[26px] font-extrabold leading-none tracking-tight text-gray-900">{result.percentage}%</div>
                  <div className={`mt-1 text-[11px] font-bold uppercase tracking-wide ${passed ? "text-green-600 dark:text-green-300" : "text-red-500 dark:text-red-300"}`}>{result.result_status}</div>
                </div>
              </div>
            </div>
            <h2 className="mt-4 text-xl font-extrabold tracking-tight text-gray-900">{passed ? "Well done!" : "Keep practising"}</h2>
            <p className="mt-1 text-[13px] text-gray-500">
              {result.questions_attempted} of {result.total_questions} attempted
              {result.submission_type === "timeout" ? " · time expired" : ""} · {fmtClock(result.time_taken_seconds || 0)}
            </p>
          </div>
        ) : (
          <div className="px-6 pt-8 pb-6 text-center sm:px-10" style={{ background: `linear-gradient(180deg, ${primary}12, transparent)` }}>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl text-white ring-1 ring-white/25" style={{ background: grad(primary), boxShadow: `0 14px 28px -12px ${primary}, inset 0 1px 0 rgba(255,255,255,.28)` }}>
              <Check className="w-8 h-8" />
            </div>
            <h2 className="mt-4 text-xl font-extrabold tracking-tight text-gray-900">Test submitted</h2>
            <p className="mt-1 text-[13px] text-gray-500">
              Your responses have been recorded
              {result.submission_type === "timeout" ? " · time expired" : ""} · {fmtClock(result.time_taken_seconds || 0)}
            </p>
          </div>
        )}

        <div className="px-6 pb-7 sm:px-10">
          {showScore ? (
            <div className="grid grid-cols-3 gap-3">
              <ResultStat value={result.correct} label="Correct" tone="green" />
              <ResultStat value={result.wrong} label="Wrong" tone="red" />
              <ResultStat value={result.unanswered} label="Skipped" tone="gray" />
            </div>
          ) : null}
          <button onClick={onDone} className={`w-full rounded-xl px-4 py-3.5 text-sm font-bold text-white transition-all hover:brightness-[1.06] active:scale-[.99] ${showScore ? "mt-6" : ""}`} style={{ background: btnBg(primary), boxShadow: `0 14px 28px -10px ${primary}` }}>
            Back to tests
          </button>
        </div>
      </div>

      {showReview && Array.isArray(result.review) && result.review.length > 0 ? (
        <div className="mt-5 space-y-3">
          <h3 className="px-1 text-sm font-bold text-gray-700">Answer review</h3>
          {result.review.map((r) => (
            <div key={r.index} className={`${CARD} p-4`}>
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg text-white ${r.is_correct ? "bg-green-500" : "bg-red-500"}`}>
                  {r.is_correct ? <Check className="w-3.5 h-3.5" /> : <span className="text-[13px] font-bold leading-none">✕</span>}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-gray-900">
                    <span className="mr-1.5 text-gray-400">{r.index}.</span>
                    {r.question}
                  </p>
                  <div className="mt-2 space-y-1 text-[13px]">
                    <p className={r.is_correct ? "text-green-600 dark:text-green-300" : "text-red-500"}>
                      Your answer: <b>{r.your_answer ?? "— not answered —"}</b>
                    </p>
                    {!r.is_correct ? <p className="text-green-600 dark:text-green-300">Correct answer: <b>{r.correct_answer}</b></p> : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
function ResultStat({ value, label, tone }) {
  const map = {
    green: "from-green-50 to-surface text-green-600 dark:from-green-500/15 dark:text-green-300 ring-green-100 dark:ring-green-500/30",
    red: "from-red-50 to-surface text-red-500 dark:from-red-500/15 dark:text-red-300 ring-red-100 dark:ring-red-500/30",
    gray: "from-gray-50 to-surface text-gray-500 ring-gray-100",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-b ring-1 ${map[tone]} py-4 text-center shadow-[0_1px_2px_rgba(16,24,40,.04)] dark:shadow-none`}>
      <div className="text-2xl font-extrabold tracking-tight">{value}</div>
      <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
    </div>
  );
}
